import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Cpu, Users, Eraser, Check, Copy, Share2, Home, RotateCcw, KeyRound, Trophy, Coins, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

const PALETTE = [
  { color: "#e0405b", name: "Rood" },
  { color: "#f5871f", name: "Oranje" },
  { color: "#f4c40f", name: "Geel" },
  { color: "#2bb673", name: "Groen" },
  { color: "#2f7ff0", name: "Blauw" },
  { color: "#9b5de5", name: "Paars" },
];

const empty = (n) => Array(n).fill(null);

function Peg({ idx, size = 44, onClick, selected }) {
  if (idx == null || idx < 0)
    return (
      <button onClick={onClick} type="button" disabled={!onClick}
        className={`rounded-full shrink-0 ${onClick ? "active:scale-90 transition" : ""}`}
        style={{ width: size, height: size, background: "var(--t-surface2)", boxShadow: "inset 0 2px 5px rgba(0,0,0,0.22)", border: selected ? "2px solid var(--t-accent)" : "1px solid var(--t-border)" }} />
    );
  const p = PALETTE[idx];
  return (
    <button onClick={onClick} type="button" disabled={!onClick}
      className={`rounded-full shrink-0 ${onClick ? "active:scale-90 transition" : ""}`}
      style={{
        width: size, height: size, background: p.color,
        backgroundImage: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.55), rgba(255,255,255,0) 55%)",
        boxShadow: `inset 0 -3px 6px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.25)${selected ? ", 0 0 0 3px var(--t-accent)" : ""}`,
      }} />
  );
}

function Feedback({ black, white, slots }) {
  const dots = [];
  for (let i = 0; i < black; i++) dots.push("white");
  for (let i = 0; i < white; i++) dots.push("black");
  while (dots.length < slots) dots.push("empty");
  return (
    <div className="grid grid-cols-2 gap-1.5 shrink-0">
      {dots.map((d, i) => (
        <span key={i} className="w-3.5 h-3.5 rounded-full"
          style={d === "white" ? { background: "#fdfdfd", boxShadow: "inset 0 -1px 2px rgba(0,0,0,0.25)", border: "1px solid rgba(0,0,0,0.18)" }
            : d === "black" ? { background: "#0d1320", boxShadow: "inset 0 -1px 2px rgba(255,255,255,0.18)" }
              : { background: "transparent", border: "1px solid var(--t-border)" }} />
      ))}
    </div>
  );
}

function PaletteRow({ held, onPick, size = 40, idPrefix = "mm-color" }) {
  return (
    <div className="flex justify-center gap-2.5 sm:gap-3">
      {PALETTE.map((p, i) => (
        <button key={i} onClick={() => onPick(i)} data-testid={`${idPrefix}-${i}`} title={p.name} className="rounded-full">
          <Peg idx={i} size={size} onClick={() => {}} selected={held === i} />
        </button>
      ))}
    </div>
  );
}

function Legend({ slots }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 mb-5">
      <ThemedCard className="p-4">
        <p className="font-heading text-xs font-extrabold tracking-widest mb-3" style={{ color: "var(--t-muted)" }}>KLEUREN</p>
        <div className="grid grid-cols-3 gap-y-3 gap-x-2">
          {PALETTE.map((p, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <Peg idx={i} size={34} />
              <span className="text-[11px] font-bold" style={{ color: "var(--t-muted)" }}>{p.name}</span>
            </div>
          ))}
        </div>
      </ThemedCard>
      <ThemedCard className="p-4">
        <p className="font-heading text-xs font-extrabold tracking-widest mb-2" style={{ color: "var(--t-muted)" }}>HOE SPEEL JE?</p>
        <p className="text-sm font-semibold mb-3" style={{ color: "var(--t-text)" }}>Kraak de geheime code van {slots} kleuren. Na elke gok krijg je feedback:</p>
        <div className="flex items-center gap-2.5 mb-2">
          <span className="w-4 h-4 rounded-full shrink-0" style={{ background: "#fdfdfd", boxShadow: "inset 0 -1px 2px rgba(0,0,0,0.25)", border: "1px solid rgba(0,0,0,0.18)" }} />
          <p className="text-sm font-semibold" style={{ color: "var(--t-text)" }}><b>WIT</b> = juiste kleur op de juiste plaats</p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="w-4 h-4 rounded-full shrink-0" style={{ background: "#0d1320", boxShadow: "inset 0 -1px 2px rgba(255,255,255,0.2)", border: "1px solid var(--t-border)" }} />
          <p className="text-sm font-semibold" style={{ color: "var(--t-text)" }}><b>ZWART</b> = juiste kleur op de verkeerde plaats</p>
        </div>
      </ThemedCard>
    </div>
  );
}

export default function Mastermind() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const joinParam = params.get("code");

  const [view, setView] = useState(joinParam ? "joining" : "menu");
  const [game, setGame] = useState(null);
  const [sel, setSel] = useState(empty(4));
  const [makeCode, setMakeCode] = useState(empty(4));
  const [held, setHeld] = useState(null);
  const [joinInput, setJoinInput] = useState("");
  const [chatText, setChatText] = useState("");
  const [busy, setBusy] = useState(false);
  const chatRef = useRef(null);

  const slots = game?.slots || 4;
  const muted = { color: "var(--t-muted)" };

  useEffect(() => { if (joinParam) doJoin(joinParam); }, []); // eslint-disable-line

  // Live polling voor versus (codemaker ziet zetten + pinnen, beiden zien chat)
  useEffect(() => {
    if (view !== "board" || !game || game.mode !== "versus") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/child/mastermind/${game.game_id}`);
        setGame((prev) => (prev && prev.game_id === data.game_id ? { ...data } : prev));
      } catch {}
    }, 2500);
    return () => clearInterval(id);
  }, [view, game?.game_id, game?.mode]); // eslint-disable-line

  useEffect(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" }); }, [game?.chat?.length]);

  const reset = () => { setSel(empty(slots)); setHeld(null); };

  const placeInto = (arr, setArr, i) => {
    const n = [...arr];
    if (held != null) { n[i] = held; sfx.select(); }
    else if (n[i] != null) { n[i] = null; sfx.click(); }
    setArr(n);
  };

  const startSolo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/mastermind/new", { mode: "solo" });
      setGame(data); setView("board"); setSel(empty(data.slots)); setHeld(null); sfx.go();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const createVersus = async () => {
    if (makeCode.some((c) => c == null)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/child/mastermind/new", { mode: "versus", code: makeCode });
      setGame(data); setView("share"); sfx.win();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doJoin = async (code) => {
    setBusy(true); setView("joining");
    try {
      const { data } = await api.get(`/child/mastermind/join/${code.trim().toUpperCase()}`);
      if (data.not_found) { toast.error("Geen spel gevonden met die code"); setView("menu"); setBusy(false); return; }
      setGame(data); setSel(empty(data.slots)); setHeld(null); setView("board");
      if (data.role === "codemaker") sfx.coin(); else sfx.go();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setView("menu"); }
    setBusy(false);
  };

  const submitGuess = async () => {
    if (sel.some((c) => c == null) || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/child/mastermind/${game.game_id}/guess`, { guess: sel });
      setGame(data); reset();
      if (data.status === "won") sfx.win();
      else if (data.status === "lost") sfx.wrong();
      else if (data.last?.black > 0) sfx.correct();
      else sfx.click();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const sendChat = async () => {
    const t = chatText.trim();
    if (!t) return;
    setChatText("");
    try { const { data } = await api.post(`/child/mastermind/${game.game_id}/chat`, { text: t }); setGame(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  // ---------- MENU ----------
  if (view === "menu")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="mm-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Codekraker 🎨</h1>
          </div>
          <p className="font-semibold mb-6" style={muted}>Kraak de geheime kleurcode! Na elke gok krijg je hints: <b style={{ color: "var(--t-text)" }}>wit</b> = goede kleur op de goede plek, <b style={{ color: "var(--t-text)" }}>zwart</b> = goede kleur, verkeerde plek.</p>

          <button onClick={startSolo} disabled={busy} data-testid="mm-solo-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Cpu className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Tegen de computer</h3><p className="text-sm font-semibold" style={muted}>De computer bedenkt een code — kraak hem in 10 beurten!</p></div>
          </button>

          <button onClick={() => { setMakeCode(empty(4)); setHeld(null); setView("make"); }} data-testid="mm-versus-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-primary)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Users className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Daag een vriend uit</h3><p className="text-sm font-semibold" style={muted}>Maak een geheime code en deel een spelcode met een vriend.</p></div>
          </button>

          <ThemedCard className="p-5">
            <h3 className="font-heading font-extrabold mb-2 flex items-center gap-2 themed-heading"><KeyRound className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Doe mee met een code</h3>
            <div className="flex gap-2">
              <input value={joinInput} onChange={(e) => setJoinInput(e.target.value.toUpperCase())} placeholder="bijv. K7M2P" maxLength={6} data-testid="mm-join-input"
                className="flex-1 h-12 rounded-xl px-4 font-heading font-black tracking-widest text-lg border-2 outline-none"
                style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-text)" }} />
              <Button onClick={() => joinInput.trim() && doJoin(joinInput)} disabled={busy || !joinInput.trim()} data-testid="mm-join-btn"
                className="h-12 rounded-xl font-bold border-0 px-5" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>Mee doen</Button>
            </div>
          </ThemedCard>
        </div>
      </ThemedShell>
    );

  // ---------- MAKE ----------
  if (view === "make")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => setView("menu")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="mm-make-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Maak je geheime code</h1>
          </div>
          <p className="font-semibold mb-5" style={muted}>Tik op een kleur en daarna op een vakje om hem te plaatsen. Tik nogmaals op een vakje om te wissen.</p>

          <ThemedCard className="p-6 mb-5">
            <div className="flex justify-center gap-2.5 mb-6" data-testid="mm-make-row">
              {Array.from({ length: 4 }).map((_, i) => (
                <Peg key={i} size={52} idx={makeCode[i] ?? -1} onClick={() => placeInto(makeCode, setMakeCode, i)} />
              ))}
            </div>
            <PaletteRow held={held} onPick={(i) => { setHeld(held === i ? null : i); sfx.click(); }} size={42} idPrefix="mm-make-color" />
            <p className="text-center text-xs font-bold mt-3" style={muted}>{held != null ? `${PALETTE[held].name} gekozen — tik op een vakje` : "Kies een kleur"}</p>
          </ThemedCard>

          <Button onClick={createVersus} disabled={makeCode.some((c) => c == null) || busy} data-testid="mm-create-btn"
            className="w-full py-6 rounded-2xl font-bold text-lg border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
            <Share2 className="w-5 h-5 mr-2" /> Maak spel & deel code
          </Button>
        </div>
      </ThemedShell>
    );

  // ---------- SHARE ----------
  if (view === "share")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-10 pb-16 text-center">
          <div className="text-5xl mb-3">🎉</div>
          <h1 className="font-heading text-2xl font-black themed-heading">Spel klaar!</h1>
          <p className="font-semibold mt-2 mb-6" style={muted}>Deel deze code met je vriend. Daarna kun je live zijn zetten volgen en chatten!</p>
          <ThemedCard className="p-7 mb-5">
            <p className="text-sm font-bold mb-2" style={muted}>Spelcode</p>
            <div className="font-heading text-5xl font-black tracking-[0.3em] themed-heading" data-testid="mm-share-code">{game.share_code}</div>
            <div className="flex gap-2 mt-5">
              <Button onClick={() => { navigator.clipboard?.writeText(game.share_code); toast.success("Code gekopieerd!"); }} data-testid="mm-copy-code"
                className="flex-1 h-11 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Copy className="w-4 h-4 mr-1.5" /> Code</Button>
              <Button onClick={() => { const url = `${window.location.origin}/leerling/codekraker?code=${game.share_code}`; navigator.clipboard?.writeText(url); toast.success("Link gekopieerd!"); }} data-testid="mm-copy-link"
                className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Share2 className="w-4 h-4 mr-1.5" /> Link</Button>
            </div>
          </ThemedCard>
          <div className="flex gap-2">
            <Button onClick={() => doJoin(game.share_code)} data-testid="mm-view-game" className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Volg het spel live</Button>
            <Button onClick={() => navigate("/leerling")} className="flex-1 h-11 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Dashboard</Button>
          </div>
        </div>
      </ThemedShell>
    );

  if (view === "joining" || !game)
    return <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>;

  // ---------- BOARD ----------
  const finished = game.status !== "playing";
  const isCodemaker = game.role === "codemaker";
  const won = game.status === "won";
  const used = game.guesses.length;
  const canPlay = !finished && !isCodemaker;
  const activeRow = canPlay ? used : -1;

  return (
    <ThemedShell>
      <div className="max-w-lg mx-auto px-4 sm:px-5 pt-6 pb-44">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="mm-board-back"><ArrowLeft className="w-5 h-5" /></Button>
          <span className="font-heading font-extrabold text-lg themed-heading">Codekraker 🎨</span>
          <span className="font-heading font-extrabold text-sm" style={muted} data-testid="mm-attempts">{game.attempts_left} over</span>
        </div>

        {game.mode === "versus" && (
          <ThemedCard className="px-4 py-2.5 mb-4 text-center">
            <p className="text-sm font-bold" style={muted}>
              {isCodemaker
                ? <>Jouw code · <b style={{ color: "var(--t-text)" }}>{game.guesser_name || "wachten op rader…"}</b> {game.guesser_name ? "raadt live" : ""}</>
                : <>Code van <b style={{ color: "var(--t-text)" }}>{game.codemaker_name || "een vriend"}</b></>}
            </p>
          </ThemedCard>
        )}

        <Legend slots={slots} />

        <ThemedCard className="p-3 sm:p-4">
          <div className="flex items-center px-1 pb-2 mb-1 border-b" style={{ borderColor: "var(--t-border)" }}>
            <span className="w-7" />
            <span className="flex-1 text-center font-heading text-xs font-extrabold tracking-widest" style={muted}>{isCodemaker ? "ZETTEN VAN DE RADER" : "JOUW GOK"}</span>
            <span className="w-[72px] text-center font-heading text-xs font-extrabold tracking-widest" style={muted}>HINT</span>
          </div>

          <div className="space-y-1.5">
            {Array.from({ length: game.max_guesses }).map((_, r) => {
              const past = game.guesses[r];
              const isActive = r === activeRow;
              const codeRow = past ? past.guess : (isActive ? sel : []);
              return (
                <div key={r} data-testid={`mm-row-${r}`}
                  className="flex items-center rounded-2xl px-2 py-2"
                  style={{ background: isActive ? "var(--t-surface2)" : "transparent", outline: isActive ? "2px solid var(--t-accent)" : "none" }}>
                  <span className="w-7 text-center font-heading font-black text-sm" style={muted}>{r + 1}</span>
                  <div className="flex-1 flex justify-center gap-2 sm:gap-2.5">
                    {Array.from({ length: slots }).map((_, i) => (
                      <Peg key={i} size={36} idx={(codeRow[i] ?? -1)} onClick={isActive ? () => placeInto(sel, setSel, i) : undefined} />
                    ))}
                  </div>
                  <div className="w-[72px] flex justify-center">
                    {past ? <Feedback black={past.black} white={past.white} slots={slots} />
                      : <div className="grid grid-cols-2 gap-1.5">{Array.from({ length: slots }).map((_, i) => <span key={i} className="w-3.5 h-3.5 rounded-full" style={{ border: "1px solid var(--t-border)" }} />)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </ThemedCard>

        {(finished || isCodemaker) && (
          <div className="flex items-center justify-center gap-2.5 mt-4">
            <span className="text-sm font-bold mr-1" style={muted}>{isCodemaker ? "Jouw code:" : "Code:"}</span>
            {Array.from({ length: slots }).map((_, i) => <Peg key={i} size={30} idx={game.secret?.[i] ?? -1} />)}
          </div>
        )}

        <AnimatePresence>
          {finished && (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <ThemedCard className="p-6 text-center mt-4" style={{ borderColor: won ? "var(--t-success)" : "var(--t-primary)" }} data-testid="mm-result">
                <div className="text-5xl mb-2">{won ? "🏆" : "🙈"}</div>
                <h2 className="font-heading text-2xl font-black themed-heading">
                  {isCodemaker ? (won ? "Je code is gekraakt!" : "Je code hield stand! 💪") : (won ? "Gekraakt!" : "Niet gelukt…")}
                </h2>
                {game.reward && (
                  <div className="flex justify-center gap-4 mt-4">
                    <span className="flex items-center gap-1.5 font-heading font-extrabold" style={{ color: "var(--t-accent)" }}><Trophy className="w-5 h-5" /> +<CountUp end={game.reward.xp_earned} /> XP</span>
                    <span className="flex items-center gap-1.5 font-heading font-extrabold" style={{ color: "var(--t-primary)" }}><Coins className="w-5 h-5" /> +<CountUp end={game.reward.coins_earned} /></span>
                  </div>
                )}
                {game.reward?.race_ticket_earned && <p className="mt-2 text-sm font-bold" style={muted}>🏎️ Je verdiende een race-ticket!</p>}
                <div className="flex gap-2 mt-5">
                  <Button onClick={() => { setGame(null); setSel(empty(4)); setView("menu"); }} data-testid="mm-again-btn" className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een keer</Button>
                  <Button onClick={() => navigate("/leerling")} className="flex-1 h-11 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Klaar</Button>
                </div>
              </ThemedCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat (altijd open bij versus) */}
        {game.mode === "versus" && (
          <ThemedCard className="p-3 mt-4" data-testid="mm-chat">
            <p className="font-heading text-xs font-extrabold tracking-widest mb-2 flex items-center gap-1.5" style={muted}><MessageCircle className="w-3.5 h-3.5" /> CHAT</p>
            <div ref={chatRef} className="h-40 overflow-y-auto space-y-1.5 mb-2 pr-1">
              {(!game.chat || game.chat.length === 0) && <p className="text-sm py-3 text-center" style={muted}>Nog geen berichten. Zeg hallo! 👋</p>}
              {(game.chat || []).map((m, i) => (
                <div key={i} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[80%] rounded-2xl px-3 py-1.5" style={{ background: m.mine ? "var(--t-primary)" : "var(--t-surface2)", color: m.mine ? "var(--t-on-primary)" : "var(--t-text)" }}>
                    {!m.mine && <span className="block text-[10px] font-bold opacity-70">{m.sender}</span>}
                    <span className="text-sm font-semibold break-words">{m.text}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                placeholder="Typ een bericht…" maxLength={300} data-testid="mm-chat-input"
                className="flex-1 h-11 rounded-xl px-4 font-semibold border-2 outline-none" style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-text)" }} />
              <Button onClick={sendChat} disabled={!chatText.trim()} data-testid="mm-chat-send" className="h-11 rounded-xl font-bold border-0 px-4" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Send className="w-4 h-4" /></Button>
            </div>
          </ThemedCard>
        )}
      </div>

      {/* Vaste invoerbalk (rader) */}
      {canPlay && (
        <div className="fixed bottom-0 inset-x-0 border-t backdrop-blur-md z-30" style={{ background: "color-mix(in srgb, var(--t-surface) 92%, transparent)", borderColor: "var(--t-border)" }}>
          <div className="max-w-lg mx-auto px-5 py-3">
            <PaletteRow held={held} onPick={(i) => { setHeld(held === i ? null : i); sfx.click(); }} size={40} idPrefix="mm-color" />
            <p className="text-center text-[11px] font-bold mt-1.5 mb-2" style={muted}>{held != null ? `${PALETTE[held].name} gekozen — tik op een vakje` : "Kies een kleur en tik op een vakje"}</p>
            <div className="flex gap-2">
              <Button onClick={() => { setSel(empty(slots)); setHeld(null); sfx.click(); }} disabled={!sel.some((c) => c != null)} data-testid="mm-clear-btn" className="h-11 rounded-xl font-bold border-2 bg-transparent px-4" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Eraser className="w-4 h-4" /></Button>
              <Button onClick={submitGuess} disabled={sel.some((c) => c == null) || busy} data-testid="mm-guess-btn" className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Check className="w-4 h-4 mr-1.5" /> Raad!</Button>
            </div>
          </div>
        </div>
      )}
    </ThemedShell>
  );
}
